import * as React from 'react'
import { CloseIcon } from '@/tiptap/tiptap-icons/close-icon'
import '@/tiptap/tiptap-node/image-upload-node/image-upload-node.scss'
import type { NodeViewProps } from '@tiptap/react'
import { NodeViewWrapper } from '@tiptap/react'
import { CircleAlert, ImageUp } from 'lucide-react'
import { toast } from 'sonner'
import type { ImageUploadNodeStorage } from './image-upload-node-extension'

type UploadOrigin = 'manual' | 'clipboard'

export interface FileItem {
  id: string
  file: File
  progress: number
  status: 'uploading' | 'success' | 'error'
  error?: string
  url?: string
  abortController?: AbortController
}

interface UploadOptions {
  maxSize: number
  limit: number
  accept: string
  upload: (
    file: File,
    onProgress: (event: { progress: number }) => void,
    signal: AbortSignal
  ) => Promise<string>
  onSuccess?: (url: string) => void
  onError?: (error: Error) => void
  onUploadStart?: (abortController: AbortController) => void
}

function useFileUpload(options: UploadOptions) {
  const [fileItem, setFileItem] = React.useState<FileItem | null>(null)

  // 校验文件是否为图片类型
  const validateImageType = (file: File): boolean => {
    // 检查 MIME 类型
    const validImageTypes = [
      'image/jpeg',
      'image/jpg',
      'image/png',
      'image/gif',
      'image/webp',
      'image/svg+xml',
      'image/bmp',
      'image/tiff',
    ]

    const hasValidMimeType = validImageTypes.includes(file.type)

    // 额外检查文件扩展名
    const fileName = file.name.toLowerCase()
    const validExtensions = [
      '.jpg',
      '.jpeg',
      '.png',
      '.gif',
      '.webp',
      '.svg',
      '.bmp',
      '.tiff',
      '.tif',
    ]
    const hasValidExtension = validExtensions.some((ext) =>
      fileName.endsWith(ext)
    )

    return hasValidMimeType && hasValidExtension
  }

  const setClipboardValidationError = (file: File, error: Error) => {
    setFileItem({
      id: crypto.randomUUID(),
      file,
      progress: 0,
      status: 'error',
      error: error.message,
    })
    options.onError?.(error)
  }

  const uploadFile = async (
    file: File,
    origin: UploadOrigin = 'manual'
  ): Promise<string | null> => {
    // 校验文件类型
    if (!validateImageType(file)) {
      const error = new Error(
        'Invalid file type. Only image files are allowed.'
      )

      if (origin === 'clipboard') {
        setClipboardValidationError(file, error)
        return null
      }

      toast.error(
        'Please select a valid image file (JPG, PNG, GIF, WebP, SVG, BMP, TIFF)'
      )
      options.onError?.(error)
      return null
    }

    if (file.size > options.maxSize) {
      const error = new Error(
        `File size exceeds maximum allowed (${options.maxSize / 1024 / 1024}MB)`
      )

      if (origin === 'clipboard') {
        setClipboardValidationError(file, error)
        return null
      }

      toast.error(error.message)
      options.onError?.(error)
      return null
    }

    const abortController = new AbortController()
    options.onUploadStart?.(abortController)

    const newFileItem: FileItem = {
      id: crypto.randomUUID(),
      file,
      progress: 0,
      status: 'uploading',
      abortController,
    }

    setFileItem(newFileItem)

    try {
      if (!options.upload) {
        throw new Error('Upload function is not defined')
      }

      const url = await options.upload(
        file,
        (event: { progress: number }) => {
          setFileItem((prev) => {
            if (!prev) return null
            return {
              ...prev,
              progress: event.progress,
            }
          })
        },
        abortController.signal
      )

      if (!url) throw new Error('Upload failed: No URL returned')

      if (!abortController.signal.aborted) {
        setFileItem((prev) => {
          if (!prev) return null
          return {
            ...prev,
            status: 'success',
            url,
            progress: 100,
          }
        })
        options.onSuccess?.(url)
        return url
      }

      return null
    } catch (error) {
      if (!abortController.signal.aborted) {
        setFileItem((prev) => {
          if (!prev) return null
          return {
            ...prev,
            status: 'error',
            progress: 0,
            error: error instanceof Error ? error.message : 'Upload failed',
          }
        })
        options.onError?.(
          error instanceof Error ? error : new Error('Upload failed')
        )
      }
      return null
    }
  }

  const uploadFiles = async (
    files: File[],
    origin: UploadOrigin = 'manual'
  ): Promise<string | null> => {
    if (!files || files.length === 0) {
      options.onError?.(new Error('No files to upload'))
      return null
    }

    if (options.limit && files.length > options.limit) {
      toast.error(
        `Maximum ${options.limit} file${options.limit === 1 ? '' : 's'} allowed`
      )
      options.onError?.(
        new Error(
          `Maximum ${options.limit} file${options.limit === 1 ? '' : 's'} allowed`
        )
      )
      return null
    }

    const file = files[0]
    if (!file) {
      toast.error('File is undefined')
      options.onError?.(new Error('File is undefined'))
      return null
    }

    return uploadFile(file, origin)
  }

  const clearFileItem = () => {
    if (!fileItem) return

    if (fileItem.abortController) {
      fileItem.abortController.abort()
    }
    if (fileItem.url) {
      URL.revokeObjectURL(fileItem.url)
    }
    setFileItem(null)
  }

  return {
    fileItem,
    uploadFiles,
    clearFileItem,
  }
}

const CloudUploadIcon: React.FC = () => (
  <svg
    width='24'
    height='24'
    viewBox='0 0 24 24'
    className='tiptap-image-upload-icon'
    fill='currentColor'
    xmlns='http://www.w3.org/2000/svg'
  >
    <path
      d='M11.1953 4.41771C10.3478 4.08499 9.43578 3.94949 8.5282 4.02147C7.62062 4.09345 6.74133 4.37102 5.95691 4.83316C5.1725 5.2953 4.50354 5.92989 4.00071 6.68886C3.49788 7.44783 3.17436 8.31128 3.05465 9.2138C2.93495 10.1163 3.0222 11.0343 3.3098 11.8981C3.5974 12.7619 4.07781 13.5489 4.71463 14.1995C5.10094 14.5942 5.09414 15.2274 4.69945 15.6137C4.30476 16 3.67163 15.9932 3.28532 15.5985C2.43622 14.731 1.79568 13.6816 1.41221 12.5299C1.02875 11.3781 0.91241 10.1542 1.07201 8.95084C1.23162 7.74748 1.66298 6.59621 2.33343 5.58425C3.00387 4.57229 3.89581 3.72617 4.9417 3.10998C5.98758 2.4938 7.15998 2.1237 8.37008 2.02773C9.58018 1.93176 10.7963 2.11243 11.9262 2.55605C13.0561 2.99968 14.0703 3.69462 14.8919 4.58825C15.5423 5.29573 16.0585 6.11304 16.4177 7.00002H17.4999C18.6799 6.99991 19.8288 7.37933 20.7766 8.08222C21.7245 8.78515 22.4212 9.7743 22.7637 10.9036C23.1062 12.0328 23.0765 13.2423 22.6788 14.3534C22.2812 15.4644 21.5367 16.4181 20.5554 17.0736C20.0962 17.3803 19.4752 17.2567 19.1684 16.7975C18.8617 16.3382 18.9853 15.7172 19.4445 15.4105C20.069 14.9934 20.5427 14.3865 20.7958 13.6794C21.0488 12.9724 21.0678 12.2027 20.8498 11.4841C20.6318 10.7655 20.1885 10.136 19.5853 9.6887C18.9821 9.24138 18.251 8.99993 17.5001 9.00002H15.71C15.2679 9.00002 14.8783 8.70973 14.7518 8.28611C14.4913 7.41374 14.0357 6.61208 13.4195 5.94186C12.8034 5.27164 12.0427 4.75043 11.1953 4.41771Z'
      fill='currentColor'
    />
    <path
      d='M11 14.4142V21C11 21.5523 11.4477 22 12 22C12.5523 22 13 21.5523 13 21V14.4142L15.2929 16.7071C15.6834 17.0976 16.3166 17.0976 16.7071 16.7071C17.0976 16.3166 17.0976 15.6834 16.7071 15.2929L12.7078 11.2936C12.7054 11.2912 12.703 11.2888 12.7005 11.2864C12.5208 11.1099 12.2746 11.0008 12.003 11L12 11L11.997 11C11.8625 11.0004 11.7343 11.0273 11.6172 11.0759C11.502 11.1236 11.3938 11.1937 11.2995 11.2864C11.297 11.2888 11.2946 11.2912 11.2922 11.2936L7.29289 15.2929C6.90237 15.6834 6.90237 16.3166 7.29289 16.7071C7.68342 17.0976 8.31658 17.0976 8.70711 16.7071L11 14.4142Z'
      fill='currentColor'
    />
  </svg>
)

const FileIcon: React.FC = () => (
  <svg
    width='43'
    height='57'
    viewBox='0 0 43 57'
    fill='currentColor'
    className='tiptap-image-upload-dropzone-rect-primary'
    xmlns='http://www.w3.org/2000/svg'
  >
    <path
      d='M0.75 10.75C0.75 5.64137 4.89137 1.5 10 1.5H32.3431C33.2051 1.5 34.0317 1.84241 34.6412 2.4519L40.2981 8.10876C40.9076 8.71825 41.25 9.5449 41.25 10.4069V46.75C41.25 51.8586 37.1086 56 32 56H10C4.89137 56 0.75 51.8586 0.75 46.75V10.75Z'
      fill='currentColor'
      fillOpacity='0.11'
      stroke='currentColor'
      strokeWidth='1.5'
    />
  </svg>
)

const FileCornerIcon: React.FC = () => (
  <svg
    width='10'
    height='10'
    className='tiptap-image-upload-dropzone-rect-secondary'
    viewBox='0 0 10 10'
    fill='currentColor'
    xmlns='http://www.w3.org/2000/svg'
  >
    <path
      d='M0 0.75H0.343146C1.40401 0.75 2.42143 1.17143 3.17157 1.92157L8.82843 7.57843C9.57857 8.32857 10 9.34599 10 10.4069V10.75H4C1.79086 10.75 0 8.95914 0 6.75V0.75Z'
      fill='currentColor'
    />
  </svg>
)

interface ImageUploadDragAreaProps {
  onFile: (files: File[]) => void
  children?: React.ReactNode
}

const ImageUploadDragArea: React.FC<ImageUploadDragAreaProps> = ({
  onFile,
  children,
}) => {
  const [dragover, setDragover] = React.useState(false)

  const onDrop = (e: React.DragEvent<HTMLDivElement>) => {
    setDragover(false)
    e.preventDefault()
    e.stopPropagation()

    const files = Array.from(e.dataTransfer.files)
    onFile(files)
  }

  const onDragover = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault()
    setDragover(true)
  }

  const onDragleave = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault()
    setDragover(false)
  }

  return (
    <div
      className={`tiptap-image-upload-dragger ${dragover ? 'tiptap-image-upload-dragger-active' : ''}`}
      onDrop={onDrop}
      onDragOver={onDragover}
      onDragLeave={onDragleave}
    >
      {children}
    </div>
  )
}

interface ImageUploadPreviewProps {
  file: File
  progress: number
  status: 'uploading' | 'success' | 'error'
  error?: string
  showRetry?: boolean
  onRetry?: () => void
  onRemove: () => void
}

const ImageUploadPreview: React.FC<ImageUploadPreviewProps> = ({
  file,
  progress,
  status,
  error,
  showRetry = false,
  onRetry,
  onRemove,
}) => {
  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 Bytes'
    const k = 1024
    const sizes = ['Bytes', 'KB', 'MB', 'GB']
    const i = Math.floor(Math.log(bytes) / Math.log(k))
    return `${parseFloat((bytes / Math.pow(k, i)).toFixed(2))} ${sizes[i]}`
  }

  return (
    <div className='tiptap-image-upload-preview'>
      {status === 'uploading' && (
        <div
          className='tiptap-image-upload-progress'
          style={{ width: `${progress}%` }}
        />
      )}

      <div className='tiptap-image-upload-preview-content'>
        <div className='tiptap-image-upload-file-info'>
          <div className='tiptap-image-upload-file-icon'>
            <CloudUploadIcon />
          </div>
          <div className='tiptap-image-upload-details'>
            <span className='tiptap-image-upload-text'>{file.name}</span>
            <span className='tiptap-image-upload-subtext'>
              {status === 'error'
                ? error || 'Upload failed. Check your connection and try again.'
                : formatFileSize(file.size)}
            </span>
          </div>
        </div>
        <div className='tiptap-image-upload-actions'>
          {status === 'uploading' && (
            <span className='tiptap-image-upload-progress-text'>
              {progress}%
            </span>
          )}
          {status === 'error' && showRetry && onRetry && (
            <button
              type='button'
              className='tiptap-image-upload-retry-btn'
              onClick={(e) => {
                e.stopPropagation()
                onRetry()
              }}
            >
              Retry
            </button>
          )}
          <button
            type='button'
            className='tiptap-image-upload-close-btn'
            onClick={(e) => {
              e.stopPropagation()
              onRemove()
            }}
          >
            <CloseIcon />
          </button>
        </div>
      </div>
    </div>
  )
}

const ClipboardImageError: React.FC<{
  message: string
  onRemove: () => void
}> = ({ message, onRemove }) => (
  <div
    className='tiptap-image-upload-preview tiptap-image-upload-error'
    title={message}
  >
    <div className='tiptap-image-upload-preview-content'>
      <div className='tiptap-image-upload-error-message'>
        <CircleAlert aria-hidden='true' />
        <span className='tiptap-image-upload-error-copy'>
          <strong>Image could not be copied.</strong> {message}
        </span>
      </div>
      <div className='tiptap-image-upload-actions'>
        <button type='button' className='tiptap-image-upload-choose-text'>
          Choose image
        </button>
        <button
          type='button'
          aria-label='Remove unavailable image'
          className='tiptap-image-upload-close-btn'
          onClick={(event) => {
            event.stopPropagation()
            onRemove()
          }}
        >
          <CloseIcon />
        </button>
      </div>
    </div>
  </div>
)

const DropZoneContent: React.FC<{ maxSize: number }> = ({ maxSize }) => (
  <>
    <div className='tiptap-image-upload-dropzone'>
      {/* <FileIcon />
      <FileCornerIcon />
      <div className='tiptap-image-upload-icon-container'>
        <CloudUploadIcon />
      </div> */}
      <ImageUp />
    </div>

    <div className='tiptap-image-upload-content'>
      <span className='tiptap-image-upload-text'>
        Click to upload or drag and drop
      </span>
      <span className='tiptap-image-upload-subtext'>
        Maximum file size {maxSize / 1024 / 1024}MB.
      </span>
    </div>
  </>
)

export const ImageUploadNode: React.FC<NodeViewProps> = (props) => {
  const { accept, limit, maxSize, uploadId, clipboardError } = props.node.attrs
  const inputRef = React.useRef<HTMLInputElement>(null)
  const extension = props.extension
  const isClipboardUpload = typeof uploadId === 'string' && uploadId.length > 0
  const storage = props.editor.storage.imageUpload as ImageUploadNodeStorage

  const uploadOptions: UploadOptions = {
    maxSize,
    limit,
    accept,
    upload: extension.options.upload,
    onSuccess: extension.options.onSuccess,
    onError: extension.options.onError,
    onUploadStart: (abortController) => {
      if (isClipboardUpload) {
        storage.abortControllers.set(uploadId, abortController)
      }
    },
  }

  const { fileItem, uploadFiles, clearFileItem } = useFileUpload(uploadOptions)

  const replaceClipboardUpload = (url: string, filename: string): boolean => {
    let pos: number | undefined

    try {
      const currentPos = props.getPos()
      if (typeof currentPos === 'number') pos = currentPos
    } catch {
      // The node may have been removed while the upload was in flight.
    }

    const currentNode =
      pos === undefined ? null : props.editor.state.doc.nodeAt(pos)

    if (
      pos === undefined ||
      currentNode?.type.name !== 'imageUpload' ||
      currentNode.attrs.uploadId !== uploadId
    ) {
      return false
    }

    const imageNode = props.editor.schema.nodes.image?.create({
      src: url,
      alt: filename,
      title: filename,
    })
    if (!imageNode) return false

    props.editor.view.dispatch(
      props.editor.state.tr
        .replaceWith(pos, pos + currentNode.nodeSize, imageNode)
        .setMeta('addToHistory', false)
    )
    return true
  }

  const replaceClipboardUploadRef = React.useRef(replaceClipboardUpload)
  replaceClipboardUploadRef.current = replaceClipboardUpload

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files
    if (!files || files.length === 0) {
      extension.options.onError?.(new Error('No file selected'))
      return
    }
    handleUpload(Array.from(files), isClipboardUpload ? 'clipboard' : 'manual')
  }

  const handleUpload = async (
    files: File[],
    origin: UploadOrigin = 'manual'
  ) => {
    const url = await uploadFiles(files, origin)

    if (url) {
      const filename = files[0]?.name.replace(/\.[^/.]+$/, '') || 'unknown'

      if (origin === 'clipboard' && isClipboardUpload) {
        storage.completedUploads.set(uploadId, { url, filename })
        replaceClipboardUpload(url, filename)

        storage.pendingFiles.delete(uploadId)
        storage.startedUploads.delete(uploadId)
        storage.abortControllers.delete(uploadId)
        return
      }

      const pos = props.getPos()
      props.editor
        .chain()
        .focus()
        .deleteRange({ from: pos, to: pos + 1 })
        .insertContentAt(pos, [
          {
            type: 'image',
            attrs: { src: url, alt: filename, title: filename },
          },
        ])
        .run()
    }
  }

  const handleUploadRef = React.useRef(handleUpload)
  handleUploadRef.current = handleUpload

  React.useEffect(() => {
    if (!isClipboardUpload || storage.startedUploads.has(uploadId)) return

    const completedUpload = storage.completedUploads.get(uploadId)
    if (completedUpload) {
      replaceClipboardUploadRef.current(
        completedUpload.url,
        completedUpload.filename
      )
      return
    }

    const pendingFile = storage.pendingFiles.get(uploadId)
    if (!pendingFile) return

    storage.startedUploads.add(uploadId)
    void handleUploadRef.current([pendingFile], 'clipboard')

    return () => {
      storage.abortControllers.get(uploadId)?.abort()
      storage.abortControllers.delete(uploadId)
      storage.startedUploads.delete(uploadId)
    }
  }, [clipboardError, isClipboardUpload, storage, uploadId])

  const handleRemove = () => {
    clearFileItem()

    if (isClipboardUpload) {
      storage.pendingFiles.delete(uploadId)
      storage.startedUploads.delete(uploadId)
      storage.abortControllers.get(uploadId)?.abort()
      storage.abortControllers.delete(uploadId)
      storage.completedUploads.delete(uploadId)
      props.deleteNode()
    }
  }

  const handleClick = () => {
    const canChooseReplacement =
      clipboardError ||
      (fileItem?.status === 'error' && !fileItem.abortController)

    if (inputRef.current && (!fileItem || canChooseReplacement)) {
      inputRef.current.value = ''
      inputRef.current.click()
    }
  }

  return (
    <NodeViewWrapper
      className='tiptap-image-upload'
      tabIndex={0}
      onClick={handleClick}
    >
      {!fileItem && clipboardError && (
        <ClipboardImageError message={clipboardError} onRemove={handleRemove} />
      )}

      {!fileItem && !clipboardError && (
        <ImageUploadDragArea onFile={handleUpload}>
          <DropZoneContent maxSize={maxSize} />
        </ImageUploadDragArea>
      )}

      {fileItem && (
        <ImageUploadPreview
          file={fileItem.file}
          progress={fileItem.progress}
          status={fileItem.status}
          error={fileItem.error}
          showRetry={isClipboardUpload && Boolean(fileItem.abortController)}
          onRetry={() => handleUpload([fileItem.file], 'clipboard')}
          onRemove={handleRemove}
        />
      )}

      <input
        ref={inputRef}
        name='file'
        accept={accept}
        type='file'
        onChange={handleChange}
        onClick={(e: React.MouseEvent<HTMLInputElement>) => e.stopPropagation()}
      />
    </NodeViewWrapper>
  )
}
